import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { getPlayerControls } from '../../../helpers';
import { useSource } from '../../sources';
import { usePlayer } from '../PlayerProvider';
import {
  Loop,
  Next,
  PlaylistButton,
  PlayPause,
  Prev,
  Shuffle,
  VolumeComponent
} from './buttons';

export function FullPlayControls() {
  const { items } = useSource();
  const { isLive } = usePlayer();
  const {
    sourceType,
    controls: derivedControls,
    existingPlayerControls,
    playerControls
  } = useSettings();

  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const withMultipleTracks = items?.length > 1;
  const withTrackControls = controls.includes('trackControls');
  const withChangeTrackControls =
    sourceType === 'rss' || (withMultipleTracks && withTrackControls);
  const withPlaylist = controls.includes('playlist');
  const withShuffle = withMultipleTracks && controls.includes('shuffle');
  const withLoop = controls.includes('loop');
  const withVolume = controls.includes('volume');

  const withSecondaryControls =
    withPlaylist || withShuffle || withLoop || withVolume;

  if (!withTrackControls && !withSecondaryControls) {
    return null;
  }

  return (
    <Container _withMainControls={withTrackControls}>
      {withTrackControls && (
        <MainControls>
          {withChangeTrackControls && <Prev />}
          {withTrackControls && (
            <PlayPause disabled={sourceType === 'radio' && !isLive} />
          )}
          {withChangeTrackControls && <Next />}
        </MainControls>
      )}

      {withSecondaryControls && (
        <SecondaryControls _withMainControls={withTrackControls}>
          {withShuffle && <Shuffle withoutMargin />}
          {withLoop && <Loop withoutMargin />}
          {withVolume && <VolumeComponent />}
          {withPlaylist && <PlaylistButton withoutMargin />}
        </SecondaryControls>
      )}
    </Container>
  );
}

const Container = styled.div.attrs<{
  _withMainControls: boolean;
}>({
  className: createReadableClass({
    component: 'full-play-controls',
    element: 'container'
  })
})`
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  max-height: ${({ _withMainControls }) => (_withMainControls ? 36 : 24)}px;
`;

const MainControls = styled.div.attrs({
  className: createReadableClass({
    component: 'full-play-controls',
    element: 'main-controls'
  })
})`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  width: 100%;
`;

const SecondaryControls = styled.div.attrs<{
  _withMainControls: boolean;
}>({
  className: createReadableClass({
    component: 'full-play-controls',
    element: 'secondary-controls'
  })
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: ${({ _withMainControls }) =>
    _withMainControls ? 'end' : 'start'};
`;
